// ICIC_POSTER_APP_INTEGRATION_V1
import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveAssignmentInstanceLabel } from '../../utils/assignmentLabels';
import { getEffectiveToday } from '../../utils/systemTime';
import {
  getStudentAssignmentDate,
  getStudentAssignmentDueDate,
  getStudentAssignmentOpenDate
} from '../../utils/assignmentTiming';
import {
  listStudentHonorsEvents,
  honorsEventCategory
} from '../../utils/honorsEvents';
import StudentSchedule from './course/StudentSchedule';
import {
  effectiveClassroomForWeek
} from '../../utils/classroomResolution';

import {
  loadIcicScheduleItems
} from '../../utils/icic';

const date10 = value => String(value || '').slice(0, 10);

function parseDate(value) {
  const p = date10(value).split('-').map(Number);
  return p.length === 3 && p.every(Number.isFinite)
    ? new Date(p[0], p[1] - 1, p[2], 12, 0, 0)
    : null;
}
function dateString(d) {
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}
function addDays(value, n) {
  const d = parseDate(value); if (!d) return '';
  d.setDate(d.getDate() + n); return dateString(d);
}
function mondayOf(value) {
  const d = parseDate(value); if (!d) return '';
  const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); return dateString(d);
}
function weekday(value, length = 'long') {
  const d = parseDate(value); return d ? d.toLocaleDateString('en-GB', { weekday: length }) : '';
}
function formatDay(value) {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
}
function formatRange(start) {
  const a = parseDate(start), b = parseDate(addDays(start, 6)); if (!a || !b) return '';
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return a.getDate() + '–' + b.getDate() + ' ' + b.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }
  return a.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + '–' +
    b.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function courseName(course) {
  const t = Array.isArray(course?.course_titles) ? course.course_titles[0] : course?.course_titles;
  return t?.name || course?.name || 'Course';
}
function classTime(course, date) {
  const day = weekday(date), times = course?.class_times;
  if (times && typeof times === 'object' && !Array.isArray(times)) {
    return String(times[day] || times[day.toLowerCase()] || course?.class_time || '').slice(0, 5);
  }
  return String(course?.class_time || times || '').slice(0, 5);
}
function effectiveClassTime(course, week, bundle) {
  const normal =
    classTime(
      course,
      week?.class_date
    );

  if (
    course?.variable_timings_enabled !==
    true
  ) {
    return normal;
  }

  if (
    course?.group_based_attendance ===
    true
  ) {
    return String(
      bundle
        ?.attendanceTimeOverrides
        ?.[String(week?.id)] ||
      normal ||
      ''
    ).slice(0, 5);
  }

  return String(
    week?.start_time_override ||
    normal ||
    ''
  ).slice(0, 5);
}

function studentHasClass(
  course,
  week,
  bundle
) {
  if (
    course
      ?.group_based_attendance ===
    true
  ) {
    return new Set(
      (
        bundle
          ?.attendanceEnabledWeekIds ||
        []
      )
        .map(String)
    ).has(
      String(
        week?.id ||
        ''
      )
    );
  }


  return (
    week
      ?.attendance_enabled ===
    true
  );
}

function classRoom(
  course,
  week,
  bundle
) {
  return effectiveClassroomForWeek(
    course,
    week,
    bundle
      ?.attendanceRoomOverrides
      ?.[String(week?.id || '')] ||
      null
  );
}
function plain(value) {
  return String(value || '').replace(/<br\s*\/?\s*>/gi, ' ').replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ').replace(/\s+/g, ' ').trim();
}
function descriptionFor(week) {
  const v = week?.course_week_content; return plain(Array.isArray(v) ? v[0]?.description : v?.description);
}
function assignmentLabel(a, bundle) {
  const baseName =
    String(
      a?.assignment_display_name ||
      ''
    ).trim() ||
    a?.assignment_types?.name ||
    'Assignment';

  const count =
    new Set(
      (bundle.assignments || [])
        .filter(
          candidate =>
            String(
              candidate
                ?.assignment_type_id ||
              ''
            ) ===
            String(
              a
                ?.assignment_type_id ||
              ''
            )
        )
        .map(
          candidate =>
            String(
              candidate?.id ||
              ''
            )
        )
        .filter(Boolean)
    ).size;

  let label =
    resolveAssignmentInstanceLabel(
      a,
      {
        baseName,
        sameTypeCount: count
      }
    );

  if (a?.schedule_source_title) {
    label +=
      ' — ' +
      a.schedule_source_title;
  }

  return label;
}

function Loader({ session, enrolment, onBundle }) {
  const id = enrolment?.courses?.id || enrolment?.course_id || '';
  const loaded = useCallback(data => onBundle(String(id), data), [id, onBundle]);
  return id ? <StudentSchedule session={session} course={enrolment.courses} enrolment={enrolment}
    dataOnly includeHonorsEvents={false} onDataLoaded={loaded} /> : null;
}

function DateRows({ label, items }) {
  if (!items.length) return null;

  return (
    <div className="sgs-date-row">
      <b>{label}</b>

      <div>
        {items.map(x => (
          <div key={x.key}>
            {x.label}

            {x.time
              ? <span> · {x.time}</span>
              : null}

            {x.course
              ? <span> · {x.course}</span>
              : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClassCard({ item }) {
  const { bundle, week } = item;
  const readings = (bundle.weekReadings || []).filter(x => String(x.course_week_id) === String(week.id));
  const events = (bundle.courseEvents || []).filter(x =>
    String(x.course_week_id) === String(week.id) &&
    (
      !x.event_date ||
      date10(x.event_date) ===
        date10(week.class_date)
    )
  );
  return <div className="sgs-line"><div className="sgs-time">{item.time || '—'}</div><article className="sgs-card">
    <div className="sgs-head"><div><strong>{courseName(bundle.course)}</strong>{item.room ? <small>{item.room}</small> : null}</div>
      {item.holiday ? <span className="sgs-cancelled">No class</span> : null}</div>
    {item.holiday ? <p className="sgs-holiday">{item.holiday.reason || 'Class cancelled'}</p> : null}
    {descriptionFor(week) ? <p>{descriptionFor(week)}</p> : null}
    {readings.map(row => { const r = row.course_readings || {}; const rel = bundle.relevantReadingAssignments?.[String(row.id)] || [];
      return <div className="sgs-detail" key={row.id}><b>Reading</b><div>{r.drive_file_id ?
        <a href={'https://drive.google.com/file/d/' + r.drive_file_id + '/view'} target="_blank" rel="noreferrer">{r.title || 'Reading'}{r.author ? ' by ' + r.author : ''}</a> :
        <span>{r.title || 'Reading'}{r.author ? ' by ' + r.author : ''}</span>}
        {rel.length ? <small>Relevant to: {rel.join(', ')}</small> : null}</div></div>; })}
    {events.map(e => { const rel = bundle.relevantEventAssignments?.[String(e.id)] || [];
      return <div className="sgs-detail" key={e.id}><b>Course content</b><div><strong>{e.title || 'Course item'}</strong>
        {(e.event_time || e.location) ? <small>{[e.event_time ? String(e.event_time).slice(0, 5) : null, e.location].filter(Boolean).join(' · ')}</small> : null}
        {rel.length ? <small>Relevant to: {rel.join(', ')}</small> : null}</div></div>; })}
  </article></div>;
}

function SimpleItem({ item }) {
  return <div className="sgs-line"><div className="sgs-time">{item.time || '—'}</div><div className="sgs-card sgs-simple">
    <strong>{item.title}</strong>{item.meta ? <span>{item.meta}</span> : null}{item.note ? <small>{item.note}</small> : null}</div></div>;
}

function pdDueDate(course, week) {
  if (
    !course?.pd_due_day ||
    !week?.class_date ||
    course?.pd_enabled === false ||
    week?.pd_enabled === false
  ) {
    return '';
  }

  const names = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const target =
    names.indexOf(
      course.pd_due_day
    );

  const date =
    parseDate(
      week.class_date
    );

  if (!date || target < 0) {
    return '';
  }

  while (
    date.getDay() !== target
  ) {
    date.setDate(
      date.getDate() + 1
    );
  }

  return dateString(date);
}


function cqDeadline(
  course,
  week,
  bundle
) {
  if (
    !week?.class_date ||
    course?.cq_enabled === false ||
    week?.cq_enabled !== true
  ) {
    return null;
  }

  /*
   * If Variable Timings has already added
   * effectiveClassTime(), use the student's
   * effective class time. Otherwise fall back
   * to the normal course time.
   */
  const effectiveTime =
    typeof effectiveClassTime === 'function'
      ? effectiveClassTime(
          course,
          week,
          bundle
        )
      : '';

  const day =
    weekday(
      week.class_date
    );

  const normalTime =
    String(
      course?.class_times?.[day] ||
      course?.class_time ||
      ''
    ).slice(0, 5);

  const time =
    effectiveTime ||
    normalTime;

  if (!time) {
    return null;
  }

  const date =
    parseDate(
      week.class_date
    );

  if (!date) {
    return null;
  }

  const parts =
    time
      .split(':')
      .map(Number);

  if (
    parts.length !== 2 ||
    !parts.every(Number.isFinite)
  ) {
    return null;
  }

  date.setHours(
    parts[0],
    parts[1],
    0,
    0
  );

  const configuredHours =
    Number(
      course
        ?.cq_due_hours_before_class ??
      1
    );

  const hours =
    Number.isFinite(configuredHours) &&
    configuredHours >= 0
      ? configuredHours
      : 1;

  date.setTime(
    date.getTime() -
      hours *
      60 *
      60 *
      1000
  );

  return {
    date:
      dateString(date),

    time: [
      String(
        date.getHours()
      ).padStart(2, '0'),

      String(
        date.getMinutes()
      ).padStart(2, '0')
    ].join(':')
  };
}

export default function StudentGlobalSchedule({
  session,
  enrolments,
  honorsPortalState,
  compact = false
}) {
  const today =
    getEffectiveToday(
      session
    );

  const [
    weekStart,
    setWeekStart
  ] =
    useState(
      mondayOf(
        today
      )
    );

  const [
    selectedDate,
    setSelectedDate
  ] =
    useState(
      today
    );

  const [
    allWeek,
    setAllWeek
  ] =
    useState(
      false
    );

  const [
    bundles,
    setBundles
  ] =
    useState({});

  const [
    honorsEvents,
    setHonorsEvents
  ] =
    useState([]);

  const [
    icicSessions,
    setIcicSessions
  ] =
    useState([]);


  const rows =
    useMemo(
      () =>
        (
          Array.isArray(
            enrolments
          )
            ? enrolments
            : []
        )
          .filter(
            row =>
              row?.id &&
              row?.courses?.id
          ),

      [enrolments]
    );


  const courseKey =
    useMemo(
      () =>
        rows
          .map(
            row =>
              String(
                row.courses.id
              )
          )
          .sort()
          .join('|'),

      [rows]
    );


  useEffect(
    () =>
      setBundles(
        {}
      ),

    [courseKey]
  );


  useEffect(() => {
    let cancelled =
      false;


    if (
      session?.isMaster
    ) {
      setHonorsEvents(
        []
      );

      return undefined;
    }


    listStudentHonorsEvents(
      session
    )
      .then(
        result => {
          if (
            !cancelled
          ) {
            setHonorsEvents(
              (
                Array.isArray(
                  result?.events
                )
                  ? result.events
                  : []
              )
                .filter(
                  event =>
                    event
                      ?.registration
                      ?.status ===
                    'Registered'
                )
            );
          }
        }
      )
      .catch(
        error => {
          console.error(
            'Loading Honors events for global Schedule failed:',
            error
          );

          if (
            !cancelled
          ) {
            setHonorsEvents(
              []
            );
          }
        }
      );


    return () => {
      cancelled =
        true;
    };
  }, [
    session
  ]);


  useEffect(() => {
    let cancelled =
      false;

    loadIcicScheduleItems(
      session,
      'student'
    )
      .then(
        rows => {
          if (!cancelled) {
            setIcicSessions(
              Array.isArray(
                rows
              )
                ? rows
                : []
            );
          }
        }
      )
      .catch(
        error => {
          console.error(
            'Loading ICIC sessions for Student Schedule failed:',
            error
          );

          if (!cancelled) {
            setIcicSessions(
              []
            );
          }
        }
      );


    return () => {
      cancelled =
        true;
    };
  }, [
    session
  ]);


  const onBundle =
    useCallback(
      (
        id,
        data
      ) =>
        setBundles(
          current => ({
            ...current,
            [id]:
              data
          })
        ),

      []
    );


  const days =
    useMemo(
      () =>
        Array.from(
          {
            length:
              7
          },

          (
            _,
            index
          ) =>
            addDays(
              weekStart,
              index
            )
        ),

      [weekStart]
    );


  const loaded =
    Object
      .values(
        bundles
      )
      .filter(
        bundle =>
          bundle &&
          !bundle.error
      );


  const loading =
    rows.length > 0 &&
    Object.keys(
      bundles
    ).length <
      rows.length;


  const registrationAvailability =
    honorsPortalState
      ?.courseRegistration
      ?.availability ||
    {};


  const registrationWindows = [
    {
      key:
        'honors-prereg-first',

      label:
        'Honors Course Pre-registration',

      open:
        registrationAvailability
          .firstOpenDate,

      close:
        registrationAvailability
          .firstCloseDate
    },

    {
      key:
        'honors-prereg-second',

      label:
        'Honors Course Add & Freshman Enrollment',

      open:
        registrationAvailability
          .secondOpenDate,

      close:
        registrationAvailability
          .secondCloseDate
    }
  ];


  function buildDayData(
    targetDate
  ) {
    const opens = [];
    const dues = [];
    const occurs = [];
    const closes = [];
    const timed = [];


    registrationWindows
      .forEach(
        window => {
          if (
            date10(
              window.open
            ) ===
            targetDate
          ) {
            opens.push({
              key:
                window.key +
                '-open',

              label:
                window.label,

              course:
                'Tunghai Honors Program'
            });
          }


          if (
            date10(
              window.close
            ) ===
            targetDate
          ) {
            closes.push({
              key:
                window.key +
                '-close',

              label:
                window.label,

              course:
                'Tunghai Honors Program'
            });
          }
        }
      );


    loaded.forEach(
      bundle => {
        const course =
          bundle.course ||
          {};

        const name =
          courseName(
            course
          );


        (
          bundle.assignments ||
          []
        ).forEach(
          assignment => {
            if (
              assignment
                ?.assignment_types
                ?.assignment_kind ===
              'structured_form'
            ) {
              return;
            }


            const label =
              assignmentLabel(
                assignment,
                bundle
              );


            const key =
              assignment
                .student_assignment_occurrence_key ||
              assignment.id;


            const requiresSubmission =
              assignment
                ?.assignment_types
                ?.requires_submission !==
              false;


            if (
              requiresSubmission
            ) {
              if (
                getStudentAssignmentOpenDate(
                  assignment
                ) ===
                targetDate
              ) {
                opens.push({
                  key:
                    'o-' +
                    key,

                  label,

                  course:
                    name
                });
              }


              if (
                getStudentAssignmentDueDate(
                  assignment
                ) ===
                targetDate
              ) {
                dues.push({
                  key:
                    'd-' +
                    key,

                  label,

                  course:
                    name
                });
              }


              return;
            }


            if (
              getStudentAssignmentDate(
                assignment
              ) ===
              targetDate
            ) {
              occurs.push({
                key:
                  'x-' +
                  key,

                label,

                course:
                  name
              });
            }
          }
        );


        (
          bundle
            .assignmentTypeSettings ||
          []
        )
          .filter(
            setting =>
              setting
                ?.schedule_enabled ===
                true
              &&
              setting
                ?.schedule_choice_enabled ===
                true
          )
          .forEach(
            setting => {
              const matching =
                (
                  bundle.assignments ||
                  []
                )
                  .find(
                    assignment =>
                      String(
                        assignment
                          .assignment_type_id
                      ) ===
                      String(
                        setting
                          .assignment_type_id
                      )
                  );


              const baseLabel =
                String(
                  setting
                    .display_name ||
                  ''
                ).trim()
                ||
                matching
                  ?.assignment_types
                  ?.name
                ||
                'Assignment';


              const label =
                baseLabel +
                ' schedule choices';


              const key =
                String(
                  setting
                    .assignment_type_id
                );


              if (
                date10(
                  setting
                    .schedule_choice_open_date
                ) ===
                targetDate
              ) {
                opens.push({
                  key:
                    'choice-open-' +
                    key,

                  label,

                  course:
                    name
                });
              }


              if (
                date10(
                  setting
                    .schedule_choice_close_date
                ) ===
                targetDate
              ) {
                closes.push({
                  key:
                    'choice-close-' +
                    key,

                  label,

                  course:
                    name
                });
              }
            }
          );


        (
          bundle
            .structuredCheckpoints ||
          []
        ).forEach(
          checkpoint => {
            const open =
              checkpoint
                .effective_open_date ||
              checkpoint
                .open_date;


            const due =
              checkpoint
                .effective_due_date ||
              checkpoint
                .due_date;


            const label =
              checkpoint.label ||
              'Structured Form';


            if (
              date10(
                open
              ) ===
              targetDate
            ) {
              opens.push({
                key:
                  'so-' +
                  checkpoint.id,

                label,

                course:
                  name
              });
            }


            if (
              date10(
                due
              ) ===
              targetDate
            ) {
              dues.push({
                key:
                  'sd-' +
                  checkpoint.id,

                label,

                course:
                  name
              });
            }
          }
        );


        if (
          course
            .self_eval_enabled ===
          true
        ) {
          [
            [
              'Mid-Term Self-Evaluation',
              course.mid_eval_open_date,
              course.mid_eval_close_date
            ],

            [
              'Final Self-Evaluation',
              course.final_eval_open_date,
              course.final_eval_close_date
            ]
          ]
            .forEach(
              ([
                label,
                open,
                due
              ]) => {
                if (
                  date10(
                    open
                  ) ===
                  targetDate
                ) {
                  opens.push({
                    key:
                      'eo-' +
                      course.id +
                      label,

                    label,

                    course:
                      name
                  });
                }


                if (
                  date10(
                    due
                  ) ===
                  targetDate
                ) {
                  dues.push({
                    key:
                      'ed-' +
                      course.id +
                      label,

                    label,

                    course:
                      name
                  });
                }
              }
            );
        }


        (
  bundle.weeks ||
  []
)
  .filter(
    week =>
      date10(
        week.class_date
      ) ===
        targetDate
      &&
      studentHasClass(
        course,
        week,
        bundle
      )
  )
          .forEach(
            week => {
              const holiday =
                (
                  bundle.holidays ||
                  []
                )
                  .find(
                    item =>
                      date10(
                        item.date
                      ) ===
                      targetDate
                  )
                ||
                null;


              timed.push({
                key:
                  'c-' +
                  course.id +
                  '-' +
                  week.id,

                kind:
                  'class',

                time:
                  effectiveClassTime(
                    course,
                    week,
                    bundle
                  ),

                room:
                  classRoom(
                    course,
                    week,
                    bundle
                  ),

                holiday,

                bundle,

                week,

                summaryVisible:
                  true,

                summaryTitle:
                  holiday
                    ? name +
                      ' — No class'
                    : name,

                summaryCourse:
                  ''
              });
            }
          );


        const weekById =
          new Map(
            (
              bundle.weeks ||
              []
            )
              .map(
                week => [
                  String(
                    week.id
                  ),

                  week
                ]
              )
          );


        /*
         * Course events remain in detailed
         * weekday mode only.
         *
         * All Week deliberately excludes them.
         */
        (
          bundle.courseEvents ||
          []
        ).forEach(
          event => {
            const linkedWeek =
              weekById.get(
                String(
                  event
                    .course_week_id
                )
              )
              ||
              null;


            const eventDate =
              date10(
                event
                  .event_date
                ||
                linkedWeek
                  ?.class_date
              );


            if (
              eventDate !==
                targetDate
              ||
              !event
                .event_date
              ||
              eventDate ===
                date10(
                  linkedWeek
                    ?.class_date
                )
            ) {
              return;
            }


            const relevant =
              bundle
                .relevantEventAssignments
                ?.[String(
                  event.id
                )]
              ||
              [];


            timed.push({
              key:
                'ce-' +
                event.id,

              kind:
                'simple',

              time:
                String(
                  event
                    .event_time ||
                  ''
                ).slice(
                  0,
                  5
                ),

              title:
                event.title ||
                'Course item',

              meta:
                [
                  name,
                  event.location
                ]
                  .filter(
                    Boolean
                  )
                  .join(
                    ' · '
                  ),

              note:
                relevant.length
                  ? 'Relevant to: ' +
                    relevant.join(
                      ', '
                    )
                  : '',

              summaryVisible:
                false
            });
          }
        );


        const pdDates =
          new Set();


        (
          bundle.weeks ||
          []
        ).forEach(
          week => {
            const dueDate =
              pdDueDate(
                course,
                week
              );


            if (
              dueDate ===
                targetDate
              &&
              !pdDates.has(
                dueDate
              )
            ) {
              pdDates.add(
                dueDate
              );


              dues.push({
                key:
                  'pd-' +
                  course.id +
                  '-' +
                  dueDate,

                label:
                  'Philosophical Diary',

                course:
                  name
              });
            }


            const cq =
              cqDeadline(
                course,
                week,
                bundle
              );


            if (
              cq?.date ===
              targetDate
            ) {
              dues.push({
                key:
                  'cq-' +
                  course.id +
                  '-' +
                  week.id,

                label:
                  'Questions Assignment',

                time:
                  cq.time,

                course:
                  name
              });
            }
          }
        );


        (
          bundle.meetings ||
          []
        )
          .filter(
            meeting =>
              date10(
                meeting
                  .meeting_date
              ) ===
              targetDate
          )
          .forEach(
            meeting => {
              (
                Array.isArray(
                  meeting
                    .student_meeting_slots
                )
                  ? meeting
                      .student_meeting_slots
                  : []
              )
                .forEach(
                  slot => {
                    const title =
                      String(
                        meeting
                          .meeting_type ||
                        ''
                      ).trim()
                        ? String(
                            meeting
                              .meeting_type
                          ).trim()
                          +
                          ' Self-Evaluation Meeting'
                        : 'Self-Evaluation Meeting';


                    timed.push({
                      key:
                        'se-' +
                        meeting.id +
                        '-' +
                        slot.id,

                      kind:
                        'simple',

                      time:
                        String(
                          slot
                            .meeting_time ||
                          ''
                        ).slice(
                          0,
                          5
                        ),

                      title,

                      meta:
                        [
                          meeting
                            .instructor
                            ?.name
                            ? 'with ' +
                              meeting
                                .instructor
                                .name
                            : null,

                          meeting.location,

                          name
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            ' · '
                          ),

                      summaryVisible:
                        true,

                      summaryTitle:
                        title,

                      summaryCourse:
                        name
                    });
                  }
                );
            }
          );


        (
          bundle
            .instructorAppointments ||
          []
        )
          .filter(
            meeting =>
              date10(
                meeting
                  .meeting_date
              ) ===
              targetDate
          )
          .forEach(
            meeting => {
              const title =
                meeting
                  .instructor_name
                  ? 'Meeting with ' +
                    meeting
                      .instructor_name
                  : 'Instructor Meeting';


              timed.push({
                key:
                  'a-' +
                  meeting.id,

                kind:
                  'simple',

                time:
                  String(
                    meeting
                      .meeting_time ||
                    ''
                  ).slice(
                    0,
                    5
                  ),

                title,

                meta:
                  [
                    name,
                    meeting.location
                  ]
                    .filter(
                      Boolean
                    )
                    .join(
                      ' · '
                    ),

                note:
                  meeting.topic ||
                  '',

                summaryVisible:
                  true,

                summaryTitle:
                  title,

                summaryCourse:
                  name
              });
            }
          );
      }
    );


    honorsEvents
      .filter(
        event =>
          date10(
            event.eventDate
          ) ===
          targetDate
      )
      .forEach(
        event => {
          timed.push({
            key:
              'h-' +
              event.eventId,

            kind:
              'simple',

            time:
              String(
                event.startTime ||
                ''
              ).slice(
                0,
                5
              ),

            title:
              event.title ||
              'Honors Event',

            meta:
              [
                honorsEventCategory(event).label,
                event.location
              ].filter(Boolean).join(' · '),

            summaryVisible:
              true,

            summaryTitle:
              event.title ||
              'Honors Event',

            summaryCourse:
              'Tunghai Honors Program'
          });
        }
      );


    icicSessions
      .filter(
        session =>
          date10(
            session?.date
          ) ===
          targetDate
      )
      .forEach(
        session => {
          if (session?.scheduleKind === 'deadline') {
            dues.push({
              key:
                'icic-deadline-' +
                session.id,

              label:
                session.title ||
                'ICIC Deadline',

              course:
                'ICIC'
            });

            return;
          }

          timed.push({
            key:
              'icic-' +
              session.id,

            kind:
              'simple',

            time:
              String(
                session.start ||
                ''
              ).slice(
                0,
                5
              ),

            title:
              session.title ||
              'ICIC Session',

            meta:
              [
                'ICIC',

                session
                  .isMySession
                  ? 'My Session'
                  : null,

                session
                  .isSavedSession
                  ? 'Saved'
                  : null,

                session.room
              ]
                .filter(Boolean)
                .join(' · '),

            summaryVisible:
              true,

            summaryTitle:
              session.title ||
              'ICIC Session',

            summaryCourse:
              'ICIC'
          });
        }
      );


    opens.sort(
      (a, b) =>
        a.label.localeCompare(
          b.label,
          undefined,
          {
            numeric:
              true
          }
        )
    );


    dues.sort(
      (a, b) =>
        a.label.localeCompare(
          b.label,
          undefined,
          {
            numeric:
              true
          }
        )
    );


    occurs.sort(
      (a, b) =>
        a.label.localeCompare(
          b.label,
          undefined,
          {
            numeric:
              true
          }
        )
    );


    closes.sort(
      (a, b) =>
        a.label.localeCompare(
          b.label,
          undefined,
          {
            numeric:
              true
          }
        )
    );


    timed.sort(
      (a, b) =>
        String(
          a.time ||
          '99:99'
        )
          .localeCompare(
            String(
              b.time ||
              '99:99'
            )
          )
    );


    return {
      opens,
      dues,
      occurs,
      closes,
      timed
    };
  }


  const currentDay =
    buildDayData(
      selectedDate
    );


  const weekAgenda =
    days
      .map(
        date => ({
          date,

          ...buildDayData(
            date
          )
        })
      )
      .map(
        day => ({
          ...day,

          summaryTimed:
            day.timed
              .filter(
                item =>
                  item
                    .summaryVisible !==
                  false
              )
        })
      )
      .filter(
        day =>
          day.opens.length
          ||
          day.dues.length
          ||
          day.occurs.length
          ||
          day.closes.length
          ||
          day
            .summaryTimed
            .length
      );


  function moveWeek(
    direction
  ) {
    setWeekStart(
      current =>
        addDays(
          current,
          direction *
            7
        )
    );


    if (
      !allWeek
    ) {
      setSelectedDate(
        current =>
          addDays(
            current,
            direction *
              7
          )
      );
    }
  }


  function currentWeek() {
    setWeekStart(
      mondayOf(
        today
      )
    );


    if (
      !allWeek
    ) {
      setSelectedDate(
        today
      );
    }
  }


  function selectDay(
    date
  ) {
    setSelectedDate(
      date
    );

    setAllWeek(
      false
    );
  }


  return (
    <div className="sgs-wrap">
      <style>
        {styles}
      </style>


      {rows.map(
        enrolment => (
          <Loader
            key={
              enrolment.id
            }
            session={
              session
            }
            enrolment={
              enrolment
            }
            onBundle={
              onBundle
            }
          />
        )
      )}


      {!compact
        ? (
          <h1>
            Schedule
          </h1>
        )
        : null}


      <div className="sgs-week">
        <button
          type="button"
          onClick={() =>
            moveWeek(
              -1
            )
          }
        >
          ‹
        </button>

        <strong>
          {
            formatRange(
              weekStart
            )
          }
        </strong>

        <button
          type="button"
          onClick={() =>
            moveWeek(
              1
            )
          }
        >
          ›
        </button>
      </div>


      {weekStart !==
        mondayOf(
          today
        ) ? (
        <button
          className="sgs-current"
          type="button"
          onClick={
            currentWeek
          }
        >
          Return to this week
        </button>
      ) : null}


      <div className="sgs-days">
        <button
          type="button"
          className={
            'sgs-day sgs-all-week ' +
            (
              allWeek
                ? 'selected'
                : ''
            )
          }
          onClick={() =>
            setAllWeek(
              true
            )
          }
        >
          <span>
            All
          </span>

          <strong>
            Week
          </strong>
        </button>


        {days.map(
          date => (
            <button
              type="button"
              key={
                date
              }
              className={
                'sgs-day ' +
                (
                  !allWeek &&
                  date ===
                    selectedDate
                    ? 'selected'
                    : date ===
                        today
                      ? 'today'
                      : ''
                )
              }
              onClick={() =>
                selectDay(
                  date
                )
              }
            >
              <span>
                {
                  weekday(
                    date,
                    'short'
                  )
                }
              </span>

              <strong>
                {
                  parseDate(
                    date
                  )
                    ?.getDate()
                }
              </strong>
            </button>
          )
        )}
      </div>


      {allWeek ? (
        <>
          <h2 className="sgs-all-week-heading">
            {weekStart ===
              mondayOf(
                today
              )
              ? 'This Week'
              : formatRange(
                  weekStart
                )}
          </h2>


          {loading ? (
            <div className="sgs-empty">
              Loading schedule...
            </div>

          ) : weekAgenda.length ? (
            <div className="sgs-week-agenda">
              {weekAgenda.map(
                day => (
                  <section
                    key={
                      day.date
                    }
                    className="sgs-week-day"
                  >
                    <h3>
                      <span>
                        {
                          weekday(
                            day.date
                          )
                        }
                      </span>

                      <small>
                        {
                          parseDate(
                            day.date
                          )
                            ?.toLocaleDateString(
                              'en-GB',
                              {
                                day:
                                  'numeric',

                                month:
                                  'short'
                              }
                            )
                        }
                      </small>
                    </h3>


                    <DateRows
                      label="Opens"
                      items={
                        day.opens
                      }
                    />

                    <DateRows
                      label="Due"
                      items={
                        day.dues
                      }
                    />

                    <DateRows
                      label="Occurs"
                      items={
                        day.occurs
                      }
                    />

                    <DateRows
                      label="Closes"
                      items={
                        day.closes
                      }
                    />


                    {day
                      .summaryTimed
                      .length ? (
                      <div className="sgs-week-times">
                        {day
                          .summaryTimed
                          .map(
                            item => (
                              <div
                                className="sgs-week-time-row"
                                key={
                                  item.key
                                }
                              >
                                <b>
                                  {
                                    item.time ||
                                    '—'
                                  }
                                </b>

                                <div>
                                  <strong>
                                    {
                                      item
                                        .summaryTitle
                                      ||
                                      item.title
                                      ||
                                      'Scheduled item'
                                    }
                                  </strong>

                                  {item
                                    .summaryCourse ? (
                                    <span>
                                      {
                                        item
                                          .summaryCourse
                                      }
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                    ) : null}
                  </section>
                )
              )}
            </div>

          ) : (
            <div className="sgs-empty">
              Nothing scheduled for this week.
            </div>
          )}
        </>

      ) : (
        <>
          <h2>
            {
              formatDay(
                selectedDate
              )
            }
          </h2>


          <DateRows
            label="Opens"
            items={
              currentDay.opens
            }
          />

          <DateRows
            label="Due"
            items={
              currentDay.dues
            }
          />

          <DateRows
            label="Occurs"
            items={
              currentDay.occurs
            }
          />

          <DateRows
            label="Closes"
            items={
              currentDay.closes
            }
          />


          {loading ? (
            <div className="sgs-empty">
              Loading schedule...
            </div>

          ) : currentDay
              .timed
              .length ? (
            <div className="sgs-list">
              {currentDay
                .timed
                .map(
                  item =>
                    item.kind ===
                      'class'
                      ? (
                        <ClassCard
                          key={
                            item.key
                          }
                          item={
                            item
                          }
                        />
                      )
                      : (
                        <SimpleItem
                          key={
                            item.key
                          }
                          item={
                            item
                          }
                        />
                      )
                )}
            </div>

          ) : (
            <div className="sgs-empty">
              {currentDay
                .opens
                .length
                ||
                currentDay
                  .dues
                  .length
                ||
                currentDay
                  .occurs
                  .length
                ||
                currentDay
                  .closes
                  .length
                ? 'Nothing else scheduled for this day.'
                : 'Nothing scheduled for this day.'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = "\n.sgs-wrap{width:100%;max-width:780px;margin:0 auto;color:#243b53}.sgs-wrap>h1{margin:0 0 18px;font-size:24px}.sgs-wrap>h2{margin:14px 0 10px;font-size:18px;text-transform:uppercase}.sgs-week{display:grid;grid-template-columns:40px 1fr 40px;gap:8px;align-items:center}.sgs-week strong{text-align:center;font-size:13px}.sgs-week button{height:36px;border:1px solid #d7dee5;border-radius:8px;background:#fff;color:#215b7d;font-size:24px}.sgs-current{display:block;margin:7px auto 0;border:0;background:transparent;color:#2f6f9f;font-size:11px;font-weight:700}.sgs-days{display:grid;grid-template-columns:1.3fr repeat(7,minmax(0,1fr));gap:4px;margin:14px 0}.sgs-day{min-width:0;padding:7px 1px;border:1px solid #d7dee5;border-radius:8px;background:#fff;color:#627d98;display:flex;flex-direction:column;align-items:center}.sgs-day span{font-size:9px;font-weight:800;text-transform:uppercase}.sgs-day strong{font-size:15px}.sgs-day.today{border-color:#2f6f9f}.sgs-day.selected{background:#215b7d;border-color:#215b7d;color:#fff}.sgs-date-row{display:grid;grid-template-columns:55px 1fr;gap:8px;padding:5px 0;font-size:13px}.sgs-date-row>b{font-size:11px;color:#627d98}.sgs-date-row span{color:#829ab1}.sgs-list{display:grid;gap:10px;margin-top:10px}.sgs-line{display:grid;grid-template-columns:48px minmax(0,1fr);gap:10px}.sgs-time{text-align:right;padding-top:11px;color:#627d98;font-size:12px;font-weight:800}.sgs-card{border:1px solid #d7dee5;border-radius:10px;background:#fff;padding:12px;min-width:0}.sgs-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}.sgs-head>div,.sgs-simple{display:flex;flex-direction:column;gap:3px}.sgs-head small,.sgs-simple span,.sgs-simple small{color:#627d98}.sgs-card p{font-size:13px;line-height:1.45;color:#334e68}.sgs-cancelled{padding:3px 7px;border-radius:999px;background:#faeaea;color:#a12a2a;font-size:9px;font-weight:800}.sgs-holiday{color:#a12a2a!important}.sgs-detail{display:grid;grid-template-columns:82px 1fr;gap:8px;border-top:1px solid #edf1f4;margin-top:9px;padding-top:8px;font-size:12px}.sgs-detail>b{font-size:9px;color:#829ab1;text-transform:uppercase}.sgs-detail>div{display:flex;flex-direction:column;gap:2px}.sgs-detail a{color:#2f6f9f;font-weight:700;text-decoration:none}.sgs-detail small{color:#8a5b00}.sgs-empty{margin-top:10px;padding:14px;border:1px solid #d7dee5;border-radius:10px;background:#f7f9fa;color:#66788a;font-size:13px}.sgs-all-week-heading{margin-top:18px!important}.sgs-week-agenda{display:grid;gap:18px;margin-top:10px}.sgs-week-day{border-top:1px solid #d7dee5;padding-top:12px}.sgs-week-day:first-child{border-top:0;padding-top:0}.sgs-week-day h3{display:flex;align-items:baseline;gap:8px;margin:0 0 7px;color:#102a43;font-size:15px;text-transform:uppercase}.sgs-week-day h3 small{color:#829ab1;font-size:11px;font-weight:700;text-transform:none}.sgs-week-times{display:grid;gap:5px;margin-top:6px}.sgs-week-time-row{display:grid;grid-template-columns:48px minmax(0,1fr);gap:10px;font-size:13px}.sgs-week-time-row>b{text-align:right;color:#627d98;font-size:12px}.sgs-week-time-row>div{display:flex;align-items:baseline;gap:7px;min-width:0}.sgs-week-time-row strong{font-size:13px}.sgs-week-time-row span{color:#829ab1;font-size:12px}@media(max-width:600px){.sgs-days{grid-template-columns:repeat(7,minmax(0,1fr))}.sgs-all-week{grid-column:1/-1;flex-direction:row!important;justify-content:center;gap:5px}.sgs-line{grid-template-columns:42px minmax(0,1fr);gap:7px}.sgs-detail{grid-template-columns:70px 1fr}.sgs-week-time-row{grid-template-columns:42px minmax(0,1fr);gap:7px}}\n";
